import { useContext, useState } from "react";
import { alertCircle, checkmarkCircle } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import {
  IonButton,
  IonIcon,
  IonInput,
  IonLabel,
  IonSpinner
} from "@ionic/react";

import { contractActions } from "../../api/ContractActions";
import { updateContextActions } from "../../api/UpdateContextActions";
import { userActions } from "../../api/UserActions";
import { CompanyHeader } from "../../components/CompanyHeader";
import { toastAdd } from "../../components/CustomToast/CustomToasts";
import FormFeedback from "../../components/UI/FormFeedback";
import AuthContext from "../../context/AuthContext";
import UserContext from "../../context/UserContext";
import type {
  AddContract,
  ContractIdentifierType,
  RegisterData
} from "../../types/requestTypes";
import type {
  Company,
  ValidateContractResponse
} from "../../types/responseTypes";
import type { ExceptionDto } from "../../types/schema";
import { normalizeClientCode } from "../../utils/normalizeClientCode";

import styles from "../../pages/4.Contracts/AddContract/AddContractPage.module.scss";

interface AddContractSubmitProps {
  source: "contracts" | "register";
  data?: RegisterData;
  selectedCompany: Company;
  contractNumber: string;
  invoiceNumber: string;
  contractAccount: string;
  identifierType: ContractIdentifierType;
  contractDetails: ValidateContractResponse;
  setContractDetails: (value: ValidateContractResponse) => void;
  setStep?: (value) => void;
}

const AddContractSubmit: React.FC<AddContractSubmitProps> = ({
  source,
  data,
  selectedCompany,
  contractNumber,
  invoiceNumber,
  contractAccount,
  identifierType,
  contractDetails,
  setContractDetails,
  setStep
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { updateMenuCtx } = useContext(UserContext);
  const { langCtx } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const usesContractAccount = selectedCompany?.id === 1;
  const activeIdentifierType = usesContractAccount
    ? identifierType
    : "invoiceNumber";
  const activeIdentifierValue =
    activeIdentifierType === "contractAccount"
      ? contractAccount
      : invoiceNumber;
  const requestIdentifierValue =
    activeIdentifierType === "contractAccount"
      ? normalizeClientCode(activeIdentifierValue)
      : activeIdentifierValue;
  const invoiceDate = contractDetails?.invoiceDate;
  const hasInvoiceDate =
    typeof invoiceDate === "string" &&
    invoiceDate.length > 0 &&
    Number.isFinite(Date.parse(invoiceDate));

  const submitHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (source === "contracts") {
        // user logat, la adaugare contract
        const data: AddContract = {
          companyId: selectedCompany?.id,
          contractNumber: contractNumber,
          ...(activeIdentifierType === "invoiceNumber" && requestIdentifierValue
            ? { invoiceNumber: requestIdentifierValue }
            : {}),
          ...(activeIdentifierType === "contractAccount" &&
          requestIdentifierValue
            ? { contractAccount: requestIdentifierValue }
            : {})
        };
        await contractActions.add(data).then(() => setIsLoading(false));
        await updateContextActions
          .getMenuDetails()
          .then((data) => updateMenuCtx(data))
          .then(() => {
            toastAdd(t("contract.added"), "success");
            history.push("/contracts");
          });
      } else {
        // creare cont nou cu adaugare contract
        await userActions.registerAccount(data).then(() => {
          setIsLoading(false);
          setStep((prevState) => prevState + 1);
        });
      }
    } catch (err) {
      setIsLoading(false);
      const ex: ExceptionDto = err.response.data;
      setErrorMsg(t([`err.${ex.message}`, "err.__"]));
      if (ex.message === "USER_ALREADY_HAS_CONTRACT") {
        setContractDetails(null);
      }
    }
  };

  return (
    <>
      <CompanyHeader company={selectedCompany} />

      <form className={styles.verificationForm} onSubmit={submitHandler}>
        <IonLabel>{t("contract.no")}</IonLabel>
        <IonInput
          data-testid="contractNo"
          value={contractNumber}
          type="text"
          inputmode="text"
          autocomplete="off"
          placeholder={t("placeholder.addHere")}
          className={errorMsg ? "error" : "success"}
          readonly
        >
          <IonIcon icon={errorMsg ? alertCircle : checkmarkCircle} />
        </IonInput>

        <IonLabel>
          {activeIdentifierType === "contractAccount"
            ? t("contract.accountNo")
            : t("contract.invoiceNo")}
        </IonLabel>
        <IonInput
          data-testid={`${activeIdentifierType}-input`}
          value={activeIdentifierValue}
          type="text"
          inputmode="text"
          autocomplete="off"
          placeholder={t("placeholder.addHere")}
          className={errorMsg ? "error" : "success"}
          readonly
        >
          <IonIcon icon={errorMsg ? alertCircle : checkmarkCircle} />
        </IonInput>

        {contractDetails ? (
          <div className={styles.contractDetails}>
            <IonLabel>{t("contract.name")}</IonLabel>
            <p>{contractDetails?.contractName}</p>

            <IonLabel>{t("contract.address")}</IonLabel>
            <p>{contractDetails?.address}</p>

            {hasInvoiceDate ? (
              <>
                <IonLabel>{t("contract.lastInvoice")}</IonLabel>
                <p>
                  {new Date(invoiceDate).toLocaleDateString(langCtx)} -{" "}
                  {contractDetails.invoiceValue.toLocaleString(langCtx)}{" "}
                  {t("currency")}
                </p>
              </>
            ) : activeIdentifierType === "contractAccount" ? (
              <>
                <IonLabel>{t("contract.id")}</IonLabel>
                <p>{contractDetails.contractId}</p>
              </>
            ) : null}
          </div>
        ) : (
          ""
        )}

        <IonButton
          data-testid="submitBtn"
          className="ag-cta"
          type="submit"
          expand="block"
          fill="solid"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <IonSpinner name="circles" /> {t("adding")}
            </>
          ) : (
            t("add")
          )}
        </IonButton>

        {errorMsg ? <FormFeedback type={"error"}>{errorMsg}</FormFeedback> : ""}
      </form>
    </>
  );
};

export default AddContractSubmit;
